/**
 * VideoOverlay - Vídeo en directo durante la partida online
 * 501 Dart League Online
 * 
 * Desktop: Dos cámaras lado a lado (rival + tú) integradas en el layout.
 * Mobile: PiP flotante solo con cámara del rival.
 * 
 * Incluye controles de audio y alertas de cámara perdida.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';

const VideoOverlay = ({
  localVideoRef,
  remoteVideoRef,
  localStreamRef,
  phase,
  myAudioMuted,
  rivalAudioMuted,
  cameraLost,
  toggleMyAudio,
  toggleRivalAudio,
  remoteHasAudio = true,
  audioAutoplayBlocked = false,
  retryRemoteAudio,
  rivalName,
  myName,
  className = '',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [cameraLostTimer, setCameraLostTimer] = useState(null);
  const [localCamAlive, setLocalCamAlive] = useState(true);
  const [showPeek, setShowPeek] = useState(false);
  const peekTimerRef = useRef(null);
  const containerRef = useRef(null);
  const dragState = useRef({ active: false, moved: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  // ─── Drag para mover el PiP en móvil ───
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const isMobile = () => window.innerWidth <= 768;
    const DRAG_THRESHOLD = 8; // px mínimo para contar como arrastre

    const onTouchStart = (e) => {
      if (!isMobile()) return;
      const touch = e.touches[0];
      const rect = el.getBoundingClientRect();
      dragState.current = {
        active: true,
        moved: false,
        startX: touch.clientX,
        startY: touch.clientY,
        origX: rect.left,
        origY: rect.top,
      };
    };

    const onTouchMove = (e) => {
      const ds = dragState.current;
      if (!ds.active) return;
      const touch = e.touches[0];
      const dx = touch.clientX - ds.startX;
      const dy = touch.clientY - ds.startY;

      // Solo activar drag si se supera el umbral
      if (!ds.moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
      ds.moved = true;

      let newX = ds.origX + dx;
      let newY = ds.origY + dy;

      // Mantener dentro de la pantalla
      const rect = el.getBoundingClientRect();
      newX = Math.max(0, Math.min(window.innerWidth - rect.width, newX));
      newY = Math.max(0, Math.min(window.innerHeight - rect.height, newY));

      el.style.right = 'auto';
      el.style.left = newX + 'px';
      el.style.top = newY + 'px';
      e.preventDefault();
    };

    const onTouchEnd = () => {
      dragState.current.active = false;
    };

    // Bloquear click/expand si hubo arrastre real
    const onClickCapture = (e) => {
      if (dragState.current.moved) {
        e.stopPropagation();
        e.preventDefault();
        dragState.current.moved = false;
      }
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('click', onClickCapture, true); // capture phase

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  // ─── Detectar si la cámara local sigue emitiendo ───
  useEffect(() => {
    const check = () => {
      try {
        const stream = localStreamRef?.current;
        if (!stream) { setLocalCamAlive(false); return; }
        const track = stream.getVideoTracks()[0];
        setLocalCamAlive(!!track && track.readyState === 'live' && track.enabled);
      } catch {
        setLocalCamAlive(false);
      }
    };
    check();
    const id = setInterval(check, 2000);
    return () => clearInterval(id);
  }, [localStreamRef]);

  // ─── Peek: mostrar mini-preview 3 s ───
  const handlePeek = useCallback(() => {
    setShowPeek(true);
    if (peekTimerRef.current) clearTimeout(peekTimerRef.current);
    peekTimerRef.current = setTimeout(() => setShowPeek(false), 3000);
  }, []);

  // Limpiar timer al desmontar
  useEffect(() => {
    return () => { if (peekTimerRef.current) clearTimeout(peekTimerRef.current); };
  }, []);

  // Countdown para cámara perdida
  useEffect(() => {
    if (!cameraLost || cameraLost.expired) {
      setCameraLostTimer(null);
      return;
    }
    
    const startTime = Date.now();
    const timeout = cameraLost.timeout || 60000;
    
    setCameraLostTimer(Math.ceil(timeout / 1000));
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, Math.ceil((timeout - elapsed) / 1000));
      setCameraLostTimer(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [cameraLost]);

  const toggleExpand = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const isVideoActive = phase === 'connected' || phase === 'verified';

  return (
    <div ref={containerRef} className={`video-overlay-dual ${expanded ? 'expanded' : ''} ${className}`}>
      
      {/* ─── Cámaras lado a lado ─── */}
      <div className="video-dual-cameras">
        
        {/* Cámara del rival */}
        <div className={`video-dual-panel rival ${isVideoActive ? 'active' : ''}`}>
          <div className="video-dual-video-wrap" onClick={toggleExpand}>
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="video-dual-video"
            />
            {!isVideoActive && (
              <div className="video-dual-placeholder">
                <div className="video-dual-spinner"></div>
              </div>
            )}
            <div className={`video-dual-status ${isVideoActive ? 'connected' : 'connecting'}`}>
              <span className="video-dual-status-dot"></span>
              {isVideoActive ? 'EN VIVO' : 'Conectando...'}
            </div>
          </div>
          <span className="video-dual-label">📹 {rivalName}</span>
          {isVideoActive && !remoteHasAudio && (
            <span className="video-no-audio-badge" title="El rival no tiene micrófono activo">🔇 Sin audio</span>
          )}
          {isVideoActive && audioAutoplayBlocked && remoteHasAudio && (
            <button className="video-unlock-audio-btn" onClick={retryRemoteAudio} title="Activar audio del rival">
              🔊 Activar audio
            </button>
          )}
          {expanded && <span className="video-dual-hint">Click para reducir</span>}
        </div>

        {/* Cámara local (tú) — solo visible en desktop */}
        <div className="video-dual-panel local">
          <div className="video-dual-video-wrap">
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              className="video-dual-video"
            />
          </div>
          <span className="video-dual-label">📹 {myName || 'Tú'}</span>
        </div>
      </div>

      {/* ─── Controles de audio + estado cámara ─── */}
      <div className="video-overlay-controls">
        <button
          className={`video-control-btn ${myAudioMuted ? 'muted' : ''}`}
          onClick={toggleMyAudio}
          title={myAudioMuted ? 'Activar micrófono' : 'Silenciar micrófono'}
        >
          {myAudioMuted ? '🔇' : '🎙️'}
        </button>

        <button
          className={`video-control-btn ${rivalAudioMuted ? 'muted' : ''}`}
          onClick={toggleRivalAudio}
          title={rivalAudioMuted ? 'Activar audio rival' : 'Silenciar rival'}
        >
          {rivalAudioMuted ? '🔈' : '🔊'}
        </button>

        {/* Indicador de estado de tu cámara — tap para peek rápido */}
        <button
          className={`video-control-btn video-cam-status-btn ${localCamAlive ? 'alive' : 'dead'}`}
          onClick={handlePeek}
          title={localCamAlive ? 'Tu cámara activa — toca para verte' : 'Tu cámara no detectada'}
        >
          📷
          <span className={`video-cam-status-dot ${localCamAlive ? 'alive' : 'dead'}`} />
        </button>

        <button
          className="video-control-btn video-expand-btn-mobile"
          onClick={toggleExpand}
          title={expanded ? 'Reducir' : 'Ampliar'}
        >
          {expanded ? '⊖' : '⊕'}
        </button>
      </div>

      {/* ─── Mini-preview local (peek 3s) ─── */}
      {showPeek && (
        <div className="video-peek-preview" onClick={() => setShowPeek(false)}>
          <video
            ref={(node) => { if (node && localStreamRef?.current) node.srcObject = localStreamRef.current; }}
            autoPlay
            playsInline
            muted
            className="video-peek-video"
          />
          <span className="video-peek-label">Tu cámara</span>
        </div>
      )}

      {/* ─── Alerta de cámara perdida ─── */}
      {cameraLost && !cameraLost.expired && (
        <div className="video-overlay-camera-lost">
          <div className="video-camera-lost-content">
            <span className="video-camera-lost-icon">⚠️</span>
            <div>
              <p className="video-camera-lost-title">Cámara de {cameraLost.player} perdida</p>
              <p className="video-camera-lost-timer">
                {cameraLostTimer ?? '…'}s para restaurar
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Cámara timeout (partida nula) */}
      {cameraLost?.expired && (
        <div className="video-overlay-camera-timeout">
          <span>📹❌</span>
          <p>{cameraLost.message || 'Cámara no restaurada. Partida nula.'}</p>
        </div>
      )}
    </div>
  );
};

export default VideoOverlay;
